import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { BookUser, Plus, Pencil, Trash2, Phone, Mail, Search, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Palette de couleurs bien distinctes, attribuées de façon stable à chaque
// site (toujours la même couleur pour un même site), pour repérer plus
// facilement à quel site appartient chaque contact quand "Tous les sites"
// est sélectionné.
const SITE_COLOR_PALETTE = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500',
  'bg-rose-500', 'bg-teal-500', 'bg-orange-500', 'bg-indigo-500',
];
const getSiteColorClass = (siteId) => {
  if (!siteId) return SITE_COLOR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < siteId.length; i++) hash = (hash * 31 + siteId.charCodeAt(i)) % SITE_COLOR_PALETTE.length;
  return SITE_COLOR_PALETTE[Math.abs(hash) % SITE_COLOR_PALETTE.length];
};

// Ancien libellé "Mme" normalisé en "MME." pour les contacts déjà enregistrés,
// tant qu'ils n'ont pas été réenregistrés avec la nouvelle valeur.
const normalizeCivility = (civility) => (civility === 'Mme' || civility === 'MME' ? 'MME.' : civility);

const emptyForm = (siteId) => ({
  site_id: siteId || '',
  civility: 'M.',
  name: '',
  function: '',
  phone: '',
  email: '',
  notes: '',
  visibility: 'everyone'
});

// Ajoute automatiquement un espace tous les 2 chiffres (format français
// lisible : "06 05 04 03 02"), sans bloquer la saisie d'un numéro étranger
const formatPhoneInput = (value) => {
  const digits = value.replace(/[^\d+]/g, '');
  const hasPlus = digits.startsWith('+');
  const cleanDigits = hasPlus ? digits.slice(1) : digits;
  const groups = cleanDigits.match(/.{1,2}/g) || [];
  return (hasPlus ? '+' : '') + groups.join(' ');
};

const ContactList = ({ contacts, loading, selectedSite, getSiteName, canManage, openEditForm, handleDelete, emptyMessage }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }
  if (contacts.length === 0) {
    return (
      <Card className="glass-effect">
        <CardContent className="py-12 text-center">
          <BookUser className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-300">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {contacts.map(c => (
        <Card key={c.id} className="glass-effect" data-testid={`contact-${c.id}`}>
          <CardContent className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-800 dark:text-slate-100">{c.civility ? `${normalizeCivility(c.civility)} ` : ''}{c.name}</p>
                {c.function && <Badge variant="outline">{c.function}</Badge>}
                {selectedSite === 'all' && (
                  <Badge className={`${getSiteColorClass(c.site_id)} text-white text-[10px]`}>{getSiteName(c.site_id)}</Badge>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 flex-wrap">
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                    <Phone className="h-3.5 w-3.5" /> {c.phone}
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:underline">
                    <Mail className="h-3.5 w-3.5" /> {c.email}
                  </a>
                )}
              </div>
              {c.notes && <p className="text-xs text-slate-400 mt-1">{c.notes}</p>}
            </div>
            {canManage && (
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => openEditForm(c)} data-testid={`edit-contact-${c.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(c.id)} data-testid={`delete-contact-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Annuaire = ({ user, onLogout }) => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const canManage = user?.permission_overrides?.manage_directory ?? ['admin', 'chef_poste'].includes(user?.role);

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line
  }, [selectedSite]);

  const loadSites = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSites(data);
        // Un non-admin n'a pas la possibilité de choisir "Tous les sites" :
        // son site (unique, ou celui choisi à la connexion) est sélectionné directement.
        if (user?.role !== 'admin' && data.length > 0) {
          setSelectedSite(prev => (prev === 'all' ? data[0].id : prev));
        }
      }
    } catch { /* silencieux */ }
  };

  const loadContacts = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const params = selectedSite !== 'all' ? `?site_id=${selectedSite}` : '';
      const res = await fetch(`${BACKEND_URL}/api/directory-contacts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setContacts(await res.json());
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  const openNewForm = () => {
    setEditingId(null);
    setFormData(emptyForm(selectedSite !== 'all' ? selectedSite : sites[0]?.id));
    setShowForm(true);
  };

  const openEditForm = (c) => {
    setEditingId(c.id);
    setFormData({
      site_id: c.site_id, civility: normalizeCivility(c.civility) || 'M.', name: c.name, function: c.function || '',
      phone: c.phone || '', email: c.email || '', notes: c.notes || '', visibility: c.visibility || 'everyone'
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.site_id || !formData.name.trim()) {
      toast.error('Le site et le nom du contact sont obligatoires');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/directory-contacts/${editingId}`
        : `${BACKEND_URL}/api/directory-contacts`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success(editingId ? 'Contact modifié' : 'Contact ajouté');
        setShowForm(false);
        loadContacts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Supprimer ce contact de l\'annuaire ?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/directory-contacts/${contactId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Contact supprimé');
        loadContacts();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name || 'Site inconnu';

  const filteredContacts = contacts.filter(c =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.function?.toLowerCase().includes(search.toLowerCase())
  );
  const everyoneContacts = filteredContacts.filter(c => c.visibility !== 'chef_poste');
  // L'annuaire chef de poste n'est de toute façon jamais renvoyé par le
  // serveur pour les autres rôles, mais on filtre aussi ici par sécurité.
  const chefPosteContacts = canManage ? filteredContacts.filter(c => c.visibility === 'chef_poste') : [];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="annuaire-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Annuaire
            </h1>
            <p className="text-gray-600 dark:text-slate-300">Contacts utiles du site, triés par ordre alphabétique</p>
          </div>
          {canManage && (
            <Button className="btn-primary" onClick={openNewForm} data-testid="new-contact-btn">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un contact
            </Button>
          )}
        </div>

        <Card className="glass-effect">
          <CardContent className="pt-6 flex flex-col md:flex-row gap-3">
            <div className="w-full md:w-64">
              <Label className="text-xs">Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="annuaire-site-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {user?.role === 'admin' && <SelectItem value="all">Tous les sites</SelectItem>}
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs">Rechercher</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Nom ou fonction..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="annuaire-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <ContactList
          contacts={everyoneContacts}
          loading={loading}
          selectedSite={selectedSite}
          getSiteName={getSiteName}
          canManage={canManage}
          openEditForm={openEditForm}
          handleDelete={handleDelete}
          emptyMessage="Aucun contact dans l'annuaire pour le moment"
        />

        {canManage && (
          <div className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-500" />
              <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100" style={{ fontFamily: 'Space Grotesk' }}>
                Annuaire Chef de Poste
              </h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Numéros réservés aux chefs de poste et administrateurs, invisibles pour les agents, comptes de site et clients.
            </p>
            <ContactList
              contacts={chefPosteContacts}
              loading={loading}
              selectedSite={selectedSite}
              getSiteName={getSiteName}
              canManage={canManage}
              openEditForm={openEditForm}
              handleDelete={handleDelete}
              emptyMessage="Aucun contact réservé aux chefs de poste pour le moment"
            />
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier le contact' : 'Ajouter un contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Site</Label>
              <Select value={formData.site_id} onValueChange={(v) => setFormData({ ...formData, site_id: v })}>
                <SelectTrigger data-testid="contact-form-site"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nom</Label>
              <div className="flex gap-2">
                <Select value={formData.civility} onValueChange={(v) => setFormData({ ...formData, civility: v })}>
                  <SelectTrigger className="w-24 shrink-0" data-testid="contact-form-civility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M.">M.</SelectItem>
                    <SelectItem value="MME.">MME.</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Ex: Marie Dupont"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="flex-1"
                  data-testid="contact-form-name"
                />
              </div>
            </div>
            <div>
              <Label>Fonction</Label>
              <Input
                placeholder="Ex: Responsable de magasin, Technicien ascenseur..."
                value={formData.function}
                onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                data-testid="contact-form-function"
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                placeholder="Ex: 06 05 04 03 02"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: formatPhoneInput(e.target.value) })}
                data-testid="contact-form-phone"
              />
            </div>
            <div>
              <Label>Email (facultatif)</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes (facultatif)</Label>
              <Input
                placeholder="Ex: Disponible en journée uniquement"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
            <div>
              <Label>Visibilité</Label>
              <Select value={formData.visibility} onValueChange={(v) => setFormData({ ...formData, visibility: v })}>
                <SelectTrigger data-testid="contact-form-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Accessible à tout le monde</SelectItem>
                  <SelectItem value="chef_poste">Entre chefs de poste uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button className="flex-1 btn-primary" onClick={handleSubmit} disabled={saving} data-testid="submit-contact-btn">
                {saving ? 'Enregistrement...' : (editingId ? 'Enregistrer' : 'Ajouter')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Annuaire;
